import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { Camera, RotateCw, Check, X, Battery, Wifi, Bluetooth, Volume2, Vibrate, MonitorSmartphone, FileText, AlertCircle, CheckCircle, PlayCircle, ShieldIcon } from 'lucide-react';
import { Button } from './ui/Button';
type ChecklistItem = 'screen' | 'housing' | 'port' | 'button' | 'camera';
type TestStatus = 'not-tested' | 'passed' | 'failed';
interface HardwareTest {
  status: TestStatus;
  value: string | null;
  component: string;
  icon: React.ReactNode;
}
const getRandomTestResult = (testKey: string) => {
  const passed = Math.random() > 0.3; // 70% chance to pass
  const values = {
    battery: passed ? `${Math.floor(Math.random() * 30 + 70)}%` : `${Math.floor(Math.random() * 30 + 40)}%`,
    wifi: passed ? 'Connected' : 'Connection issues',
    bluetooth: passed ? 'Functional' : 'Not responding',
    camera: passed ? 'Functional' : 'Issues detected',
    speaker: passed ? 'Functional' : 'No output',
    vibration: passed ? 'Functional' : 'Not working',
    display: passed ? 'Functional' : 'Display issues'
  };
  return {
    status: passed ? 'passed' : 'failed',
    value: values[testKey as keyof typeof values]
  };
};
const HardwareTestItem = ({
  icon,
  label,
  test
}) => {
  return <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 hover:border-blue-500 dark:hover:border-brand-green transition-colors" data-id="element-561">
      <div className="flex items-center justify-between" data-id="element-562">
        <div className="flex items-center" data-id="element-563">
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded-lg mr-3" data-id="element-564">
            {icon}
          </div>
          <span className="font-medium text-gray-900 dark:text-white" data-id="element-565">
            {label}
          </span>
        </div>
        <div className="flex items-center" data-id="element-566">
          {test.status !== 'not-tested' && <span className={`mr-3 ${test.status === 'passed' ? 'text-blue-600 dark:text-brand-green' : 'text-red-600'}`} data-id="element-567">
              {test.value}
            </span>}
          {test.status === 'not-tested' ? <span className="text-gray-500 text-sm" data-id="element-568">Not tested</span> : test.status === 'passed' ? <CheckCircle className="text-blue-600 dark:text-brand-green" size={16} data-id="element-569" /> : <AlertCircle className="text-red-600" size={16} data-id="element-570" />}
        </div>
      </div>
    </div>;
};
const ComponentStatus = ({
  icon,
  name,
  status,
  detail
}) => {
  return <div className="bg-white dark:bg-gray-800 rounded-lg p-4 flex flex-col space-y-2 border border-gray-200 dark:border-gray-700" data-id="element-571">
      <div className="flex items-center justify-between mb-2" data-id="element-572">
        <div className="flex items-center space-x-3" data-id="element-573">
          <div className={`${getStatusColor(status)}`} data-id="element-574">{icon}</div>
          <span className="font-medium text-gray-900 dark:text-white" data-id="element-575">
            {name}
          </span>
        </div>
      </div>
      <div className={`flex items-center justify-between ${getStatusColor(status)}`} data-id="element-576">
        {getStatusIcon(status)}
        <span className="text-sm ml-2" data-id="element-577">{detail || status}</span>
      </div>
    </div>;
};
export function DiagnosticsModule() {
  const [activeTab, setActiveTab] = useState<'visual' | 'hardware'>('visual');
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [visualChecklist, setVisualChecklist] = useState<Record<ChecklistItem, TestStatus>>({
    screen: 'not-tested',
    housing: 'not-tested',
    port: 'not-tested',
    button: 'not-tested',
    camera: 'not-tested'
  });
  const [hardwareTests, setHardwareTests] = useState<Record<string, HardwareTest>>({
    battery: {
      status: 'not-tested',
      value: null,
      component: 'Battery Health',
      icon: <Battery className="h-4 w-4" data-id="element-578" />
    },
    wifi: {
      status: 'not-tested',
      value: null,
      component: 'WiFi',
      icon: <Wifi className="h-4 w-4" data-id="element-579" />
    },
    bluetooth: {
      status: 'not-tested',
      value: null,
      component: 'Bluetooth',
      icon: <Bluetooth className="h-4 w-4" data-id="element-580" />
    },
    camera: {
      status: 'not-tested',
      value: null,
      component: 'Camera',
      icon: <Camera className="h-4 w-4" data-id="element-581" />
    },
    speaker: {
      status: 'not-tested',
      value: null,
      component: 'Speaker',
      icon: <Volume2 className="h-4 w-4" data-id="element-582" />
    },
    vibration: {
      status: 'not-tested',
      value: null,
      component: 'Vibration',
      icon: <Vibrate className="h-4 w-4" data-id="element-583" />
    },
    display: {
      status: 'not-tested',
      value: null,
      component: 'Display',
      icon: <MonitorSmartphone className="h-4 w-4" data-id="element-584" />
    }
  });
  const [runningTests, setRunningTests] = useState(false);
  useEffect(() => {
    startCamera();
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [facingMode]);
  const startCamera = async () => {
    try {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode
        }
      });
      setStream(newStream);
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
    }
  };
  const switchCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };
  const takePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(videoRef.current, 0, 0);
      }
    }
  };
  const updateVisualCheck = (key: ChecklistItem, value: TestStatus) => {
    setVisualChecklist(prev => ({
      ...prev,
      [key]: value === prev[key] ? 'not-tested' : value
    }));
  };
  const runHardwareTests = async () => {
    setRunningTests(true);
    const tests = Object.keys(hardwareTests);
    for (const test of tests) {
      const result = getRandomTestResult(test);
      await new Promise(resolve => setTimeout(resolve, 500));
      setHardwareTests(prev => ({
        ...prev,
        [test]: {
          ...prev[test],
          status: result.status as TestStatus,
          value: result.value
        }
      }));
    }
    setRunningTests(false);
  };
  const getTestsCompleted = () => Object.values(hardwareTests).filter(test => test.status !== 'not-tested').length;
  const getTestsPassed = () => Object.values(hardwareTests).filter(test => test.status === 'passed').length;
  const getOverallCondition = () => {
    const passed = getTestsPassed();
    const total = getTestsCompleted();
    if (total === 0) return 'Not Tested';
    const ratio = passed / total;
    if (ratio >= 0.8) return 'Good';
    if (ratio >= 0.6) return 'Fair';
    return 'Poor';
  };
  return <div className="bg-gray-50 dark:bg-black p-6 rounded-xl" data-id="element-585">
      <div className="space-y-8" data-id="element-586">
        <div className="flex justify-between items-center" data-id="element-587">
          <div className="flex space-x-4" data-id="element-588">
            <button onClick={() => setActiveTab('visual')} className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'visual' ? 'bg-blue-600 dark:bg-brand-green text-white dark:text-black' : 'text-gray-600 dark:text-gray-400'}`} data-id="element-589">
              Visual Check
            </button>
            <button onClick={() => setActiveTab('hardware')} className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'hardware' ? 'bg-blue-600 dark:bg-brand-green text-white dark:text-black' : 'text-gray-600 dark:text-gray-400'}`} data-id="element-590">
              Hardware Test
            </button>
          </div>
        </div>
        {activeTab === 'visual' && <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-591">
            <div data-id="element-592">
              <h3 className="font-semibold mb-4 text-gray-900 dark:text-white" data-id="element-593">
                Camera Feed
              </h3>
              <div className="relative aspect-video bg-black rounded-lg overflow-hidden mb-4" data-id="element-594">
                <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" data-id="element-595" />
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-596">
                  <div className="text-white text-sm bg-black/50 px-3 py-1 rounded-full" data-id="element-597">
                    Click to capture
                  </div>
                </div>
              </div>
              <div className="flex space-x-3" data-id="element-598">
                <Button onClick={takePhoto} className="flex-1 bg-blue-600 dark:bg-brand-green text-white dark:text-black" data-id="element-599">
                  <Camera className="h-4 w-4 mr-2" data-id="element-600" />
                  Take Photo
                </Button>
                <Button onClick={switchCamera} variant="outline" data-id="element-601">
                  <RotateCw className="h-4 w-4 mr-2" data-id="element-602" />
                  Switch Camera
                </Button>
              </div>
            </div>
            <div data-id="element-603">
              <h3 className="font-semibold mb-4 text-gray-900 dark:text-white" data-id="element-604">
                Visual Inspection Checklist
              </h3>
              <div className="space-y-4" data-id="element-605">
                {[{
              key: 'screen',
              label: 'Screen Damage'
            }, {
              key: 'housing',
              label: 'Housing Damage'
            }, {
              key: 'port',
              label: 'Port Condition'
            }, {
              key: 'button',
              label: 'Button Function'
            }, {
              key: 'camera',
              label: 'Camera Lens'
            }].map(({
              key,
              label
            }) => <div key={key} className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800" data-id="element-606">
                    <div className="flex items-center justify-between" data-id="element-607">
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-608">
                        {label}
                      </span>
                      <div className="flex space-x-2" data-id="element-609">
                        <button onClick={() => updateVisualCheck(key as ChecklistItem, 'passed')} className={`p-2 rounded-full ${visualChecklist[key as ChecklistItem] === 'passed' ? 'bg-blue-100 dark:bg-brand-green/20 text-blue-600 dark:text-brand-green' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`} data-id="element-610">
                          <Check className="h-4 w-4" data-id="element-611" />
                        </button>
                        <button onClick={() => updateVisualCheck(key as ChecklistItem, 'failed')} className={`p-2 rounded-full ${visualChecklist[key as ChecklistItem] === 'failed' ? 'bg-red-100 dark:bg-red-900/20 text-red-600' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`} data-id="element-612">
                          <X className="h-4 w-4" data-id="element-613" />
                        </button>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>}
        {activeTab === 'hardware' && <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-614">
            <div data-id="element-615">
              <div className="flex items-center justify-between mb-6" data-id="element-616">
                <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-617">
                  Hardware Diagnostics
                </h3>
                <Button onClick={runHardwareTests} disabled={runningTests} className="bg-blue-600 dark:bg-brand-green text-white dark:text-black" data-id="element-618">
                  {runningTests ? <>
                      <RotateCw className="h-4 w-4 mr-2 animate-spin" data-id="element-619" />
                      Running Tests...
                    </> : <>
                      <PlayCircle className="h-4 w-4 mr-2" data-id="element-620" />
                      Run All Tests
                    </>}
                </Button>
              </div>
              <div className="space-y-4" data-id="element-621">
                {Object.entries(hardwareTests).map(([key, test]) => <HardwareTestItem key={key} icon={test.icon} label={test.component} test={test} data-id="element-622" />)}
              </div>
            </div>
            <div data-id="element-623">
              <h3 className="font-semibold mb-6 text-gray-900 dark:text-white" data-id="element-624">
                Diagnostic Report
              </h3>
              <div className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-800" data-id="element-625">
                <div className="mb-6" data-id="element-626">
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3" data-id="element-627">
                    Device Information
                  </h4>
                  <div className="space-y-3" data-id="element-628">
                    <div className="flex justify-between items-center" data-id="element-629">
                      <span className="text-gray-500 dark:text-gray-400" data-id="element-630">
                        Model:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-631">
                        iPhone 13 Pro
                      </span>
                    </div>
                    <div className="flex justify-between items-center" data-id="element-632">
                      <span className="text-gray-500 dark:text-gray-400" data-id="element-633">
                        Serial:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-634">
                        XXXX-XXXX-XXXX
                      </span>
                    </div>
                    <div className="flex justify-between items-center" data-id="element-635">
                      <span className="text-gray-500 dark:text-gray-400" data-id="element-636">
                        OS Version:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-637">
                        iOS 15.5
                      </span>
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-800 pt-6" data-id="element-638">
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3" data-id="element-639">
                    Test Summary
                  </h4>
                  <div className="space-y-3" data-id="element-640">
                    <div className="flex justify-between items-center" data-id="element-641">
                      <span className="text-gray-500 dark:text-gray-400" data-id="element-642">
                        Tests Completed:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-643">
                        {getTestsCompleted()}/7
                      </span>
                    </div>
                    <div className="flex justify-between items-center" data-id="element-644">
                      <span className="text-gray-500 dark:text-gray-400" data-id="element-645">
                        Tests Passed:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-646">
                        {getTestsPassed()}/{getTestsCompleted()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center" data-id="element-647">
                      <span className="text-gray-500 dark:text-gray-400" data-id="element-648">
                        Overall Condition:
                      </span>
                      <span className={`font-medium ${getOverallCondition() === 'Good' ? 'text-blue-600 dark:text-brand-green' : 'text-yellow-600'}`} data-id="element-649">
                        {getOverallCondition()}
                      </span>
                    </div>
                  </div>
                  <div className={`mt-6 p-4 rounded-lg ${getOverallCondition() === 'Good' ? 'bg-blue-50 dark:bg-brand-green/10 border border-blue-100 dark:border-brand-green/20' : 'bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-100 dark:border-yellow-900/20'}`} data-id="element-650">
                    <div className="flex items-start" data-id="element-651">
                      {getOverallCondition() === 'Good' ? <CheckCircle className="text-blue-600 dark:text-brand-green mt-0.5 mr-3 flex-shrink-0" size={20} data-id="element-652" /> : <AlertCircle className="text-yellow-600 mt-0.5 mr-3 flex-shrink-0" size={20} data-id="element-653" />}
                      <div data-id="element-654">
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-655">
                          {getOverallCondition() === 'Good' ? 'Device passed diagnostic tests' : 'Device has minor issues'}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-656">
                          {getOverallCondition() === 'Good' ? 'This device is in good condition and ready for trade-in or resale' : "Some issues were detected that may affect the device's value"}
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button className="w-full mt-6 bg-blue-600 dark:bg-brand-green text-white dark:text-black" data-id="element-657">
                    <FileText className="h-4 w-4 mr-2" data-id="element-658" />
                    Generate Complete Report
                  </Button>
                </div>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}